import React, { forwardRef, useState } from "react";
import ReactFlagsSelect from "react-flags-select";
import { bool, string, node } from "prop-types";
import { twMerge } from "tailwind-merge";

import { useSolJourneySelector } from "@redux/reducers/appConfig";
import { useUserProfileSelector } from "@redux/reducers/deal";
import { useTranslation, Trans } from "@lib/i18n";
import Typography from "./Typography";
import { useDetectDevice } from "@hooks/useDetectDevice";

import { COUNTRY_CODES } from "@shared/constants";

const TelephoneField = forwardRef(function TelephoneField(props, ref) {
  const {
    required,
    className,
    fullWidth,
    label,
    disabled,
    maxLength,
    name,
    ...restProps
  } = props;

  const userProfile = useUserProfileSelector();
  const { isMobile } = useDetectDevice();

  const { countryCode } = useSolJourneySelector();
  const countries = Object.keys(COUNTRY_CODES);
  const countryCodes = Object.values(COUNTRY_CODES);
  const { t } = useTranslation();

  const isCountryCodeIncludedInPhoneNumber = () => {
    return countryCodes.some(code => userProfile.phone?.includes(code));
  };

  const initialCountryCode = countryCode => {
    if (isCountryCodeIncludedInPhoneNumber()) {
      return countryCodes.find(code => userProfile.phone.includes(code));
    }
    return countryCode;
  };

  const getKeyByValue = (object, value) => {
    return Object.keys(object).find(key => object[key] === value);
  };

  const [selected, setSelected] = useState(
    getKeyByValue(COUNTRY_CODES, initialCountryCode(countryCode))
  );

  const [countryCodeNumber, setCountryCodeNumber] = useState(
    initialCountryCode(countryCode)
  );

  const initialNumberValue = isCountryCodeIncludedInPhoneNumber()
    ? userProfile.phone.slice(initialCountryCode(countryCode).length)
    : "";

  const [phoneNumberInput, setPhoneNumberInput] = useState(
    countryCodeNumber + initialNumberValue
  );

  const handleCountryCodeNumber = (codeCode, country) => {
    setCountryCodeNumber(codeCode);
    setSelected(country);
    setPhoneNumberInput(codeCode);
  };

  const handlePhoneNumberChange = e => {
    setPhoneNumberInput(e.target.value);
  };

  const handleCountryCodeChange = code => {
    if (COUNTRY_CODES[code]) {
      handleCountryCodeNumber(COUNTRY_CODES[code], code);
    }
  };

  return (
    <label
      className={twMerge(
        "block mt-4 cursor-text",
        fullWidth && "w-full",
        className
      )}
    >
      {label}
      {required && "*"}
      <div
        className={twMerge(
          "flex items-center mt-2 border-2 border-greyLight rounded-sm text-base tracking-wide",
          disabled ? "bg-greyLight pointer-events-none" : "bg-white"
        )}
      >
        <ReactFlagsSelect
          className="
            pb-0 mr-2 -my-1 -mb-2 text-base min-w-32
            [&>button]:!border-0 [&>button]:!border-r-2 [&>button]:!border-r-greyLight [&>button]:!rounded-none [&>button]:!transform-none [&>button]:!text-sm
            [&_input]:!border [&_input]:!border-greyLight [&_input]:!rounded-sm
            [&_ul]:!text-sm
          "
          selected={selected}
          onSelect={handleCountryCodeChange}
          countries={countries}
          searchable="false"
          optionsSize={16}
          selectedSize={16}
        />
        <input
          disabled={disabled}
          className="border-0 text-sm flex-1-1-0 outline-none bg-transparent p-2 pr-0"
          maxLength={maxLength}
          name={name}
          onChange={e => handlePhoneNumberChange(e)}
          ref={ref}
          required={required}
          type={"text"}
          value={phoneNumberInput}
          {...restProps}
        />
      </div>
      {!isCountryCodeIncludedInPhoneNumber() && (
        <Typography className="text-xs text-green mt-1 mx-2">
          <Trans
            i18nKey={t("myDetails.formatPhoneNumber")}
            components={{ br: isMobile ? <></> : <br /> }}
          />
        </Typography>
      )}
    </label>
  );
});

TelephoneField.propTypes = {
  className: string,
  disabled: bool,
  fullWidth: bool,
  label: node,
  maxLength: string,
  name: string,
  required: bool,
};

export default TelephoneField;
